/**
 * EmptyStateDemo — Onboarding redesign v3 (2026-05-23, dev only).
 *
 * Workspace empty-state surface at `/`. Mounts the *real* `InputBar` and
 * `ResultPillComp` from V2InputBar / V2ResultPill, driven by fixture data,
 * so a first-time visitor sees a session that is visually indistinguishable
 * from a real one.
 *
 * ─── Watch-gate (Part A, 2026-05-27) ────────────────────────────────────────
 * First-time visitors cannot skip the demo until it has played one full loop.
 * The CTA ("Try for yourself") and all dismiss controls are hidden until the
 * `celebrate` phase fires (= demo completed). After that a localStorage flag
 * (`primate-onboarding-v3-watched`) persists the completion so the gate is
 * skipped on return visits.
 *
 * Dev / Playwright escape hatch: add `?onboarding=skip` to the URL. The param
 * is read on mount and immediately calls `markWatched()` so the gate is open.
 * The synthetic prod monitor (`demo-readiness-prod.spec.ts`) navigates with
 * this param so it isn't blocked by the watch gate.
 *
 * Flow (autoplay):
 *   1. Mount → wait 500ms.
 *   2. Type the prompt character-by-character into the textarea (~40ms/char).
 *   3. Show CompiledQueryCard skeleton (~600ms) then populated lane.
 *   4. Materialise the ResultPillComp above the composer.
 *   5. Stream narrative entries one at a time, paced by elapsed_s deltas.
 *   6. On the last narrative landing → flash "✓ answered in 11ms" chip (1.5s).
 *   7. After 3s settled → clear the textarea + restart.
 *
 * Any user interaction (keystroke on textarea, click on the input bar, source
 * change) dismisses the demo and the user is in control of an empty composer.
 *
 * Carved-out scope: this component does NOT modify V2InputBar / V2ResultPill /
 * CompiledQueryCard behaviour. It only drives them with fixture props.
 */

import {
  type CSSProperties,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { CompiledQuery, ParseState } from './compiled-query-types'
import { L, type ResultPill, type VideoSource } from './pv2-shared'
import { InputBar } from './V2InputBar'
import { ResultPillComp } from './V2ResultPill'
import { CompareToGeminiModal, type CompareToGeminiFixture } from './CompareToGeminiModal'
import { OnboardingCoach, DemoWatchNote, type OnboardingCoachPhase } from './OnboardingCoach'
import { trackCoachDismissed, trackCompareModalOpened, trackOnboardingVideoWatched, trackTryForYourselfClicked } from '../../telemetry/events'
import { OnboardingHighlights, SUBMIT_HIGHLIGHT_MS } from './OnboardingHighlights'

// ─────────────────────────────────────────────────────────────────────────────
// Fixture shape — matches public/empty-state/demo-session.json exactly.
// ─────────────────────────────────────────────────────────────────────────────

export interface EmptyStateDemoFixture {
  prompt: string
  compiledQuery: CompiledQuery
  resultPill: ResultPill
  clipPath: string
}

export interface EmptyStateDemoProps {
  /** Pre-loaded fixture. If omitted the component fetches `/empty-state/demo-session.json`. */
  fixture?: EmptyStateDemoFixture | null
  /**
   * Called when the user "touches" the demo and should take control of an
   * empty composer. The host page should reset prompt + clear any selection.
   */
  onUserTakeover?: () => void
  /**
   * Called when `[ Try for yourself ]` is clicked. Host should open the
   * VideoSourcePopover (typically via the existing `videoMenuOpen` state).
   */
  onTryForYourself?: () => void
  /**
   * Optional pre-loaded compare fixture. If omitted and the flag is enabled,
   * the modal fetches `/compare/gemini-fixture.json` on open.
   */
  compareFixture?: CompareToGeminiFixture | null
  isMobile?: boolean
  /**
   * Whether the visitor is signed in. Drives the OnboardingCoach dismiss
   * controls: anon visitors see no Skip/Hide/Don't-show-again links (per
   * 2026-05-25 onboarding spec). Signed-in users see them.
   */
  isSignedIn?: boolean
  /**
   * Called by the OnboardingCoach when the user clicks "Don't show again".
   * EmptyStateDemo also flips its own dismissed state so the surface unmounts.
   */
  onPersistentDismiss?: () => void
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers — playback state machine.
// ─────────────────────────────────────────────────────────────────────────────

type Phase =
  | 'pre-start'   // mounted, waiting initial 500ms
  | 'typing'      // characters appearing in textarea
  | 'parsing'     // CompiledQueryCard skeleton visible
  | 'lanes'       // CompiledQueryCard populated
  | 'streaming'   // ResultPillComp visible, narrative entries appearing
  | 'celebrate'   // ✓ answered chip flashed
  | 'settled'     // last narrative landed, holding for 3s
  | 'dismissed'   // user touched the demo, real composer in control

// Onboarding timing — tuned 2026-05-26 so each step has time to register.
// Originally the demo zipped from step 1 to step 4 in <2s which made the
// coach rail feel like a strobe. Retuned 2026-05-26 (Matt 14:53): "the
// cyan transition for 'submit to darwin' happens way too fast, the
// steps 3, 4, 5 need to be slowed down a lot". Then 2026-05-26 23:07
// Matt: "the onboarding video is good now, however I want to slow it
// down. Triple the time it takes for each step through the process".
// All step-pacing constants were multiplied by 3× from their 2026-05-26
// daytime values. Then 2026-05-27 Matt: "speed up by 30%" — multiplied
// again by 0.7× (= net 2.1× the original baseline).
// Companion change: SUBMIT_HIGHLIGHT_MS in OnboardingHighlights.tsx
// kept aligned with STREAMING_INITIAL_HOLD_MS.
//
// Step durations (ms). History:
//   - Original (2026-05-26 daytime):  TYPE=65, PRE=1400, PARSE=2500,
//                                      LANES=1500, HOLD=1800, PACE=1.6,
//                                      CEL=1500, SETTLE=3000
//   - PR #127 (2026-05-26 ×3):        TYPE=195, PRE=4200, PARSE=7500,
//                                      LANES=4500, HOLD=5400, PACE=4.8,
//                                      CEL=4500, SETTLE=9000
//   - PR next (2026-05-27 ×0.7):      TYPE=140, PRE=2900, PARSE=5300,
//                                      LANES=3200, HOLD=3800, PACE=3.4,
//                                      CEL=3200, SETTLE=6300
//
// Budget per step (target, post-×0.7):
//   step 1 (pre-start):              ~2.9s
//   step 2 (typing):                 ~3.2s (23 chars × 140ms)
//   step 3 (parsing + lanes):        ~8.5s (5300 parsing + 3200 lanes)
//   step 4 (streaming submit-hold):  ~3.8s (sub-phase in OnboardingHighlights)
//   step 5 (streaming + celebrate + settled): ~12–16s
const TYPE_DELAY_MS = 140
const PRE_START_DELAY_MS = 2900
const PARSING_DELAY_MS = 5300
const LANES_TO_STREAM_DELAY_MS = 3200
// Initial pause inside the streaming phase before narrative entries
// start landing. Aligns with the OnboardingHighlights submit-button
// window (3800ms) so the user has time to see "Submit to Darwin" before
// the result pill takes over the eye.
const STREAMING_INITIAL_HOLD_MS = 3800
// Narrative entries pace at this multiple of their fixture elapsed_s
// delta. 1.0 = real time; 3.4 = ~3.4× slower so each line lands with a
// readable beat (0.7× the previous 4.8 multiplier).
const NARRATIVE_PACE_MULTIPLIER = 3.4
const CELEBRATE_DURATION_MS = 3200
const SETTLE_BEFORE_LOOP_MS = 6300

// ─────────────────────────────────────────────────────────────────────────────
// Watch-gate helpers.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * localStorage key that records when the visitor first completed the demo.
 * The v3 prefix ensures a fresh gate even if older keys exist from pre-v3
 * onboarding flows.
 */
export const ONBOARDING_WATCHED_KEY = 'primate-onboarding-v3-watched'

// First-party cookie fallback for the watched flag. Mobile Safari's ITP and
// private-browsing mode can EVICT or block localStorage, which made a returning
// user (Mehdi, 2026-06-26) get re-shown the un-skippable onboarding demo on
// every refresh — the flag never persisted. A first-party cookie survives ITP
// far better than localStorage in those states, so we read/write BOTH and treat
// either as "watched". 1-year expiry; SameSite=Lax so it rides ordinary
// same-site navigations.
const ONBOARDING_WATCHED_COOKIE = 'pv_onboarding_watched'
const ONBOARDING_WATCHED_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year, seconds

function readWatchedCookie(): boolean {
  try {
    if (typeof document === 'undefined') return false
    return document.cookie
      .split(';')
      .some(c => c.trim().startsWith(`${ONBOARDING_WATCHED_COOKIE}=`))
  } catch {
    return false
  }
}

function writeWatchedCookie(value: string): void {
  try {
    if (typeof document === 'undefined') return
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
    document.cookie =
      `${ONBOARDING_WATCHED_COOKIE}=${encodeURIComponent(value)}` +
      `; Max-Age=${ONBOARDING_WATCHED_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`
  } catch {
    // Cookies disabled — localStorage (when available) remains the backstop.
  }
}

/**
 * Returns true when the visitor has completed the demo at least once.
 * Checks localStorage OR the first-party cookie fallback, so a browser that
 * evicts/blocks localStorage (mobile Safari ITP / private mode) still
 * remembers a returning user and never re-forces the un-skippable demo.
 */
export const hasWatched = (): boolean => {
  try {
    if (typeof window !== 'undefined' && !!localStorage.getItem(ONBOARDING_WATCHED_KEY)) {
      return true
    }
  } catch {
    // fall through to cookie
  }
  return readWatchedCookie()
}

/**
 * Stamps the watched flag (current ISO timestamp) in BOTH localStorage and the
 * first-party cookie. Idempotent. Writing both means eviction of one store
 * still leaves the other to remember the user.
 */
export const markWatched = (): void => {
  const stamp = new Date().toISOString()
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem(ONBOARDING_WATCHED_KEY, stamp)
    }
  } catch {
    // Private browsing / storage quota — cookie below is the fallback.
  }
  writeWatchedCookie(stamp)
}

/** True when `?onboarding=skip` is present — Playwright / synthetic monitors. */
function hasSkipParam(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('onboarding') === 'skip'
    )
  } catch {
    return false
  }
}

function isCompareFlagEnabled(): boolean {
  try {
    // import.meta.env access through a defensive bracket reference so jest/vitest
    // setups without the env still tree-shake cleanly.
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    return env?.VITE_COMPARE_TO_GEMINI_ENABLED === 'true'
  } catch {
    return false
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function EmptyStateDemo({
  fixture: providedFixture,
  onUserTakeover,
  onTryForYourself,
  compareFixture: providedCompareFixture,
  isMobile = false,
  isSignedIn = false,
  onPersistentDismiss,
}: EmptyStateDemoProps): ReactElement | null {
  // Watch-gate state — initialised from localStorage (or skip param). --------
  // demoCompleted: true → CTA + dismiss controls are visible.
  // First-time visitors start with false; the gate opens when celebrate fires.
  // demoCompleted is resolved ONCE at mount and never flipped mid-session:
  //   true  → a prior visit completed the demo (hasWatched) OR ?onboarding=skip
  //           → the "Try for yourself" CTA + dismiss controls are visible.
  //   false → first viewing → the demo is un-skippable. Completing it persists
  //           the watched flag (markWatched, in the celebrate handler) so the
  //           NEXT mount seeds this true — but the CTA never appears this session.
  // (2026-06-10, Matt: "on the first viewing it can't be skipped".)
  const [demoCompleted] = useState<boolean>(() => {
    // ?onboarding=skip: dev/Playwright escape hatch. Call markWatched() NOW
    // so that even if the component re-mounts it stays open.
    if (hasSkipParam()) {
      markWatched()
      return true
    }
    return hasWatched()
  })

  // Countdown — elapsed ms since the typing phase began (for DemoWatchNote).
  // We track the start timestamp in a ref (stable, no closure issues) and
  // update elapsedMs state every 500ms via an interval that runs while the
  // demo is active. This is NOT a parallel state machine — it’s just a
  // display-update ticker driven by the step machine’s phase transitions.
  const demoStartRef = useRef<number | null>(null)
  const [elapsedMs, setElapsedMs] = useState(0)

  // Fixture loading -----------------------------------------------------------
  const [fixture, setFixture] = useState<EmptyStateDemoFixture | null>(providedFixture ?? null)
  const [compareFixture, setCompareFixture] = useState<CompareToGeminiFixture | null>(
    providedCompareFixture ?? null,
  )
  const [fixtureError, setFixtureError] = useState<string | null>(null)
  useEffect(() => {
    if (providedFixture !== undefined) {
      setFixture(providedFixture)
      return
    }
    let cancelled = false
    fetch('/empty-state/demo-session.json')
      .then((r) => {
        if (!r.ok) throw new Error(`fixture fetch ${r.status}`)
        return r.json() as Promise<EmptyStateDemoFixture>
      })
      .then((json) => {
        if (!cancelled) setFixture(json)
      })
      .catch((err) => {
        if (!cancelled) setFixtureError(String(err))
      })
    return () => {
      cancelled = true
    }
  }, [providedFixture])

  // Lazy-load compare fixture only if flag is enabled.
  useEffect(() => {
    if (!isCompareFlagEnabled()) return
    if (providedCompareFixture !== undefined && providedCompareFixture !== null) return
    let cancelled = false
    fetch('/compare/gemini-fixture.json')
      .then((r) => (r.ok ? (r.json() as Promise<CompareToGeminiFixture>) : null))
      .then((json) => {
        if (!cancelled && json) setCompareFixture(json)
      })
      .catch(() => {/* silent — modal just won't render */})
    return () => {
      cancelled = true
    }
  }, [providedCompareFixture])

  // Playback state ------------------------------------------------------------
  const [phase, setPhase] = useState<Phase>('pre-start')
  const [displayedPrompt, setDisplayedPrompt] = useState('')
  const [visibleNarrativeCount, setVisibleNarrativeCount] = useState(0)
  // celebrateVisible state removed 2026-05-26 — the chip it gated
  // ("✓ answered in 11ms") was removed per Matt. The `celebrate` phase
  // continues to fire via setPhase('celebrate'); only the visual chip is gone.
  const [compareOpen, setCompareOpen] = useState(false)
  const timers = useRef<number[]>([])

  // Desktop-only: track viewport width so the OnboardingCoach (rendered
  // with position:fixed pinned to viewport right) only mounts when there's
  // room for it without overlapping the demo column.
  //
  // Geometry at 2026-05-26: DesktopLayout's parent is maxWidth 900 centered.
  // Demo right edge in viewport = (vw + 900) / 2. Coach width 240 + 24
  // gutter on the demo side + 24 viewport margin on the right.
  // Required vw: (vw + 900) / 2 + 24 + 240 + 24 ≤ vw  ⇒  vw ≥ 1476.
  // We round up to 1480 for breathing room.
  const [coachVisible, setCoachVisible] = useState<boolean>(true)
  useEffect(() => {
    if (isMobile) return
    const recompute = (): void => {
      setCoachVisible(window.innerWidth >= 1480)
    }
    recompute()
    window.addEventListener('resize', recompute)
    return () => window.removeEventListener('resize', recompute)
  }, [isMobile])

  // Total demo duration in ms — derived from step-machine constants + fixture.
  // This is the denominator in the DemoWatchNote countdown ("0:08 / 0:33").
  // Computed once when fixture arrives; treated as stable for the component
  // lifetime (the fixture doesn't change after load).
  const totalDemoMs = useMemo(() => {
    if (!fixture) return 30_000 // safe fallback before fixture loads
    const prompt = fixture.prompt
    const narrative = fixture.resultPill.narrative ?? []
    const firstElapsed = narrative[0]?.elapsed_s ?? 0
    const lastElapsed = narrative[narrative.length - 1]?.elapsed_s ?? firstElapsed
    const narrativeSpanMs = Math.max(0, (lastElapsed - firstElapsed) * 1000 * NARRATIVE_PACE_MULTIPLIER)
    return Math.round(
      prompt.length * TYPE_DELAY_MS + 100 +
      PARSING_DELAY_MS +
      LANES_TO_STREAM_DELAY_MS +
      STREAMING_INITIAL_HOLD_MS +
      narrativeSpanMs + 200 +
      CELEBRATE_DURATION_MS,
    )
  }, [fixture])

  const clearTimers = useCallback(() => {
    for (const t of timers.current) window.clearTimeout(t)
    timers.current = []
  }, [])
  const schedule = useCallback((fn: () => void, ms: number): void => {
    const id = window.setTimeout(fn, ms)
    timers.current.push(id)
  }, [])

  // Dismiss handler — called on any user interaction.
  const dismiss = useCallback(() => {
    clearTimers()
    setPhase('dismissed')
    setDisplayedPrompt('')
    setVisibleNarrativeCount(0)
    onUserTakeover?.()
  }, [clearTimers, onUserTakeover])

  // Record the start timestamp when typing begins (for countdown).
  useEffect(() => {
    if (phase === 'typing') {
      demoStartRef.current = Date.now()
      setElapsedMs(0)
    }
    if (phase === 'dismissed' || phase === 'pre-start') {
      demoStartRef.current = null
      setElapsedMs(0)
    }
  }, [phase])

  // Run a 500ms interval ticker while the demo is actively playing and not
  // yet completed. Stops automatically on completion or dismissal.
  useEffect(() => {
    if (demoCompleted) return
    if (phase === 'pre-start' || phase === 'dismissed') return
    const id = window.setInterval(() => {
      if (demoStartRef.current !== null) {
        setElapsedMs(Date.now() - demoStartRef.current)
      }
    }, 500)
    return () => window.clearInterval(id)
  }, [phase, demoCompleted])

  // Try-for-yourself: dismiss demo + open source picker.
  const handleTryForYourself = useCallback(() => {
    trackTryForYourselfClicked()
    clearTimers()
    setPhase('dismissed')
    onTryForYourself?.()
  }, [clearTimers, onTryForYourself])

  // The autoplay loop -------------------------------------------------------
  const runLoop = useCallback(() => {
    if (!fixture) return
    clearTimers()
    setDisplayedPrompt('')
    setVisibleNarrativeCount(0)
    setPhase('pre-start')

    schedule(() => {
      setPhase('typing')
      // Type the prompt character-by-character.
      const target = fixture.prompt
      for (let i = 1; i <= target.length; i++) {
        const idx = i
        schedule(() => setDisplayedPrompt(target.slice(0, idx)), idx * TYPE_DELAY_MS)
      }
      const typingDoneAt = target.length * TYPE_DELAY_MS
      schedule(() => setPhase('parsing'), typingDoneAt + 100)
      schedule(() => setPhase('lanes'), typingDoneAt + 100 + PARSING_DELAY_MS)
      schedule(() => setPhase('streaming'), typingDoneAt + 100 + PARSING_DELAY_MS + LANES_TO_STREAM_DELAY_MS)

      // Stream narrative entries, paced by elapsed_s deltas. The first
      // STREAMING_INITIAL_HOLD_MS of the streaming phase is reserved for the
      // submit-button highlight (coach step 4) — narrative entries don't
      // start landing until that window ends.
      const narrative = fixture.resultPill.narrative ?? []
      const streamStart = typingDoneAt + 100 + PARSING_DELAY_MS + LANES_TO_STREAM_DELAY_MS
      const firstEntryAt = narrative[0]?.elapsed_s ?? 0
      let lastLandingAt = streamStart + STREAMING_INITIAL_HOLD_MS
      narrative.forEach((entry, i) => {
        // Pace deltas relative to the first entry, then apply
        // NARRATIVE_PACE_MULTIPLIER so each line gets a readable beat.
        const offsetMs = Math.max(0, (entry.elapsed_s - firstEntryAt) * 1000 * NARRATIVE_PACE_MULTIPLIER)
        const landAt = streamStart + STREAMING_INITIAL_HOLD_MS + offsetMs
        lastLandingAt = Math.max(lastLandingAt, landAt)
        schedule(() => setVisibleNarrativeCount(i + 1), landAt)
      })

      // Celebrate after the last narrative entry. The visual chip was removed
      // 2026-05-26 per Matt; the phase transition still drives the coach.
      //
      // Watch-gate (2026-06-10, Matt): completing the demo PERSISTS the watched
      // flag (markWatched) but must NOT reveal the "Try for yourself" CTA in the
      // same session. The first viewing is fully un-skippable — the skip CTA only
      // appears on the 2nd+ mount, when `hasWatched()` is already true at mount
      // time and seeds `demoCompleted = true`. So we deliberately do NOT call
      // setDemoCompleted(true) here.
      schedule(() => {
        setPhase('celebrate')
        markWatched()
        trackOnboardingVideoWatched()
      }, lastLandingAt + 200)
      schedule(() => setPhase('settled'), lastLandingAt + 200 + CELEBRATE_DURATION_MS)

      // Loop: clear textarea + restart.
      schedule(() => {
        // Only restart if user has NOT taken over.
        setPhase((p) => (p === 'dismissed' ? 'dismissed' : 'pre-start'))
      }, lastLandingAt + 200 + CELEBRATE_DURATION_MS + SETTLE_BEFORE_LOOP_MS)
    }, PRE_START_DELAY_MS)
  }, [fixture, clearTimers, schedule])

  // Kick off the loop when fixture arrives or when the loop self-resets to
  // 'pre-start'. We deliberately do NOT depend on `phase` here — if we did,
  // every phase transition would re-run the effect cleanup and `clearTimers()`
  // would kill the in-flight typing/streaming timers.
  const fixtureReady = !!fixture
  useEffect(() => {
    if (!fixtureReady) return
    if (phase === 'pre-start') runLoop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixtureReady, phase === 'pre-start' ? 'go' : 'hold'])

  // Unmount cleanup is separate — only clears on actual unmount.
  useEffect(() => () => clearTimers(), [clearTimers])

  // Derived view props --------------------------------------------------------
  const parseState: ParseState = useMemo(() => {
    switch (phase) {
      case 'pre-start':
      case 'typing':
        return 'idle'
      case 'parsing':
        return 'lanes_visible'  // skip skeleton — show JSON immediately in the demo
      case 'lanes':
      case 'streaming':
      case 'celebrate':
      case 'settled':
        return 'lanes_visible'
      case 'dismissed':
        return 'idle'
    }
  }, [phase])

  const showResultPill = phase === 'streaming' || phase === 'celebrate' || phase === 'settled'

  // Build a partial ResultPill that only includes the narrative entries that
  // have "landed" so far — the rest of the pill structure (answer, confidence,
  // searchTerms, liveData, streamHistory) is constant.
  const livePill: ResultPill | null = useMemo(() => {
    if (!fixture) return null
    const full = fixture.resultPill
    return {
      ...full,
      narrative: full.narrative.slice(0, visibleNarrativeCount),
    }
  }, [fixture, visibleNarrativeCount])

  // The InputBar requires a `videoSource` to look "real" (the chip with the
  // file/stream name). Synthesise a faux file source from the fixture clip.
  const fakeVideoSource: VideoSource = useMemo(
    () => ({ kind: 'file', name: 'demo-warehouse.mp4', thumbUrl: undefined }),
    [],
  )

  // OnboardingCoach callbacks — declared BEFORE the early-returns to keep the
  // React hook order stable across renders (Rules of Hooks).
  const coachPhase: OnboardingCoachPhase = phase

  // Sub-phase clock inside `streaming`: drives the sticky-note step indicator
  // from step 4 → step 5 at the same moment the highlight overlay moves from
  // submit-prompt-btn → narrative-log. Without this the sticky note would
  // say STEP 4 (Submit to Darwin) while the cyan glow is already on the
  // narrative log — the exact "highlight of the wrong item" mismatch Matt
  // screenshotted 2026-05-28.
  const [streamingPastSubmitHighlight, setStreamingPastSubmitHighlight] = useState(false)
  useEffect(() => {
    if (phase !== 'streaming') {
      setStreamingPastSubmitHighlight(false)
      return
    }
    setStreamingPastSubmitHighlight(false)
    const t = window.setTimeout(
      () => setStreamingPastSubmitHighlight(true),
      SUBMIT_HIGHLIGHT_MS,
    )
    return () => window.clearTimeout(t)
  }, [phase])
  const sessionDismiss = useCallback(() => { trackCoachDismissed({ type: 'session' }); dismiss() }, [dismiss])
  const persistentDismiss = useCallback(() => {
    trackCoachDismissed({ type: 'persistent' })
    onPersistentDismiss?.()
    dismiss()
  }, [onPersistentDismiss, dismiss])

  // Render --------------------------------------------------------------------
  if (phase === 'dismissed') return null
  if (fixtureError) {
    // Fail silently — host page will fall back to its minimal empty state.
    return null
  }
  if (!fixture) return null

  const wrapperStyle: CSSProperties = {
    width: '100%',
    maxWidth: 1020,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    padding: isMobile ? '0 12px' : 0,
    position: 'relative',
  }

  // celebrateStyle removed 2026-05-26 per Matt — the "✓ answered in 11ms"
  // chip was visually competing with the coach + Compare button at the top
  // of the pill, and the inference time is already in the pill header.
  // The `celebrate` phase + celebrateVisible state are retained so the
  // demo timing loop (parsing → lanes → streaming → celebrate → settled)
  // continues to drive the coach's active-step indicator through step 5.

  const ctaStyle: CSSProperties = {
    alignSelf: 'center',
    background: L.signal,
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    padding: '10px 22px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: '0 2px 10px rgba(27,79,224,0.25)',
  }

  const compareBtnStyle: CSSProperties = {
    background: 'transparent',
    border: `1px solid ${L.border}`,
    borderRadius: 999,
    padding: '6px 14px',
    fontSize: 12,
    color: L.textPrimary,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
  }

  const showCompareButton = isCompareFlagEnabled() && compareFixture !== null

  // OnboardingCoach: render alongside the demo while it's autoplaying. The
  // coach itself self-gates on VITE_ONBOARDING_COACH_ENABLED + the persistent
  // localStorage flag, so we can mount it unconditionally and let it return
  // null when disabled. Dismiss controls only render for signed-in users.
  // (coachPhase / sessionDismiss / persistentDismiss are defined above the
  // early-returns to keep hook order stable.)

  // Onboarding v3 (2026-05-25, Matt): the "Try for yourself" CTA and the
  // "Compare to Gemini" button must (1) be visible throughout the entire demo
  // (not just after the result pill mounts), and (2) NEVER move — their
  // positions stay locked regardless of which demo phase we're in. Compare
  // also has to sit OUTSIDE the result pill card so it doesn't overlap the
  // pill's internal header icons (the artifact Matt flagged).
  //
  // Approach: the result pill wrapper is always mounted (when fixture is
  // loaded) so it reserves vertical space. visibility flips when streaming
  // begins. The Compare button is positioned absolutely to the wrapper's
  // top-right edge so it stays in the same screen position from t=0 to
  // settled, even while the pill content fades in/out beneath it.

  // Desktop: wrap the demo column in a flex row with the OnboardingCoach
  // pinned to the right. Mobile: drop the strip between the CTA and the
  // InputBar wrapper (rendered inline below). The flex-row wrapper preserves
  // the existing `data-testid="empty-state-demo"` on the demo column so all
  // pre-existing tests that locate it continue to pass.
  const demoColumn = (
    <div
      data-testid="empty-state-demo"
      style={wrapperStyle}
      /* Dismiss when the user clicks anywhere INSIDE the surface that isn't a
         primary CTA. The textarea handles its own dismiss via onPromptChange. */
      onMouseDown={(e) => {
        // Buttons handle their own clicks; don't double-dismiss for them.
        const target = e.target as HTMLElement
        if (target.closest('[data-testid="empty-state-cta"]')) return
        if (target.closest('[data-testid="empty-state-compare-btn"]')) return
      }}
    >
      {/* Result pill area — always mounted (when fixture is loaded) so the
         CTA + InputBar positions don't shift when streaming begins. visibility
         flips so the pill only appears in streaming/celebrate/settled phases
         but the reserved space stays. The Compare-to-Gemini button is pinned
         to the wrapper's top-right and is rendered INDEPENDENTLY of the pill's
         visibility so it (a) stays clickable throughout the demo and (b) sits
         OUTSIDE the result pill card, avoiding overlap with the pill's
         internal header icons (the artifact Matt flagged 2026-05-25).

         DemoWatchNote lives inside this container so it's anchored to the
         demo viewport corner (not the page).

         2026-06-10 (Matt): the dashed-blue "demo-viewport frame signal" outline
         was removed. It wrapped the camera + prompt + empty results area in a
         dotted box that clashed with the full-width workspace UX and read as a
         visual regression. The surface now renders clean/full-width like the
         real workspace.
         */}
      {livePill && (
        <div
          data-testid="demo-viewport"
          style={{
            position: 'relative',
            width: '100%',
            borderRadius: 12,
          }}
        >
          {/* DemoWatchNote — "I'm Darwin" / "Your turn" sticky indicator.
             Positioned absolutely at top-right of the demo viewport.
             pointerEvents:none in the component so it never blocks clicks.
             Hidden during active demo phases (parsing → settled) when
             demoCompleted=true to prevent "Your turn" from lingering
             while the demo is replaying (Bug 3, 2026-05-28).

             Also hidden during the RESULT phases (streaming/celebrate/settled)
             on first run: that's when the result pill mounts and the step-5
             cyan highlight glow wraps the narrative-log, whose top-right edge
             reaches the same corner as this sticky note. The glow's box-shadow
             spread bled into the note (Matt 2026-06-04: "highlight box overlaps
             with the post-it in the results pill"). The note's job is the
             pre-result walkthrough (steps 1–4); once results stream it steps
             aside so the glow has a clean corner. */}
          {/* "Your turn —" completion variant removed (Matt 2026-06-06):
             it was redundant with the blue "Try for yourself" CTA and lingered
             on prod for returning visitors. The note now only shows during the
             first-visit in-progress walkthrough (demoCompleted=false). */}
          {!demoCompleted && (phase === 'pre-start' || phase === 'typing' ||
            phase === 'parsing' || phase === 'lanes') && (
            <DemoWatchNote
              isMobile={isMobile}
              demoCompleted={demoCompleted}
              elapsedMs={elapsedMs}
              totalDemoMs={totalDemoMs}
            />
          )}
          {showCompareButton && (
            <div style={{ position: 'absolute', top: -2, right: 0, zIndex: 4 }}>
              <button
                type="button"
                data-testid="empty-state-compare-btn"
                onClick={() => { trackCompareModalOpened(); setCompareOpen(true) }}
                style={compareBtnStyle}
                aria-label="Compare to Gemini"
              >
                Compare to Gemini →
              </button>
            </div>
          )}
          {/* Reserve vertical space for the pill so the CTA below stays
             anchored. Sizing strategy (revised 2026-05-26 per Matt's
             "video pushes input pill off the bottom of the screen"):
             - Desktop: keep the worst-case 300px reserve (untouched).
             - Mobile: reserve only 220px AND cap the pill content height
               at 220px via overflow:hidden. The full pill (peak ~433px on
               mobile) doesn't need to render in onboarding — the demo's
               job there is to teach the input + coach interaction, not
               to display every narrative row. Users see the full pill
               after they tap "Try for yourself".
             - Mobile budget recomputed 2026-06-04 (input bar was being pushed
               off the bottom on iPhone 14 + Galaxy S22 after the 3-zone reserve
               was raised to 460-500). Worst case is Galaxy S22 (360×780):
                 780 viewport − ~144 chrome           = 636 available
                 − input-bar wrapper reserve (210)      = 426
                 − CTA + column gaps (~56)              = 370
                 = ~370 for the whole 3-zone pill reserve.
               The coach sticky note is NO LONGER in this budget on mobile:
               once the watch-gate opens (demoCompleted=true) it unmounts and
               the blue "Try for yourself" button is the single CTA (Bug 3 fix).
               The 3 zones now fit inside 370:
                 camera 16:9 @ ~336px inner            ≈ 189px
                 Zone 2 pinned pill clamp(110,20vh,150) ≤ 150px
                 Zone 3 thread strip (flex, can shrink) ≥ ~30px
               → reserve set to min 360 / max 400. Input bar stays above the
               fold on both iPhone 14 (844) and Galaxy S22 (780). */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              visibility: showResultPill ? 'visible' : 'hidden',
              marginTop: showCompareButton ? 36 : 0,
              // 2026-05-28 (Matt): demo must reflect actual product UX on
              // mobile during streaming — the real product uses a 3-zone
              // pinned layout (camera block + pinned narrative pill), NOT a
              // generic full pill with a "Video coming soon" placeholder.
              // Mobile reserve recomputed 2026-06-04 (input bar was pushed off
              // the bottom on iPhone 14 + Galaxy S22 by the previous 460-500
              // reserve). The mock still replicates the real product's full
              // 3-zone mobile layout — Zone 1 camera (true 16:9, ~189px at a
              // ~336px-wide demo), Zone 2 pinned active pill (now clamp
              // 110-150px), Zone 3 scrollable thread-history strip (flex,
              // shrinks to fill the remainder). camera ~189 + Zone 2 ≤150 +
              // Zone 3 ≥~30 ≈ 369 → reserve 360 min / 400 max so the input bar
              // stays above the fold (see the budget math above). Desktop
              // keeps the original 300px reserve.
              minHeight: isMobile ? 360 : 300,
              maxHeight: isMobile ? 400 : undefined,
              overflow: isMobile ? 'hidden' : undefined,
              borderRadius: isMobile ? 12 : undefined,
            }}
            aria-hidden={!showResultPill}
          >
            {/* The "✓ answered in 11ms" celebrate chip was removed 2026-05-26
               per Matt — it was visually competing with the coach mark and
               the Compare button at the top of the pill, and the inference
               time is already shown inline in the pill header. */}
            {isMobile && showResultPill ? (
              <DemoMobilePinnedMock pill={livePill} />
            ) : (
              <ResultPillComp
                pill={livePill}
                remoteStream={null}
                localStream={null}
                slotRevealed
                noFadeIn
                isMobile={isMobile}
                isStreamActive={false}
                isSaving={false}
                canDownload={false}
                planTier="free"
              />
            )}
          </div>
        </div>
      )}

      {/* CTA — gated on demoCompleted. Hidden until the demo plays one full
         loop (watch-gate). Returning visitors (flag set) see it immediately.
         The underlying handleTryForYourself handler is still defined (above
         the render path) so the parent state machines stay clean on re-mount. */}
      {demoCompleted && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            data-testid="empty-state-cta"
            onClick={handleTryForYourself}
            style={ctaStyle}
          >
            Try for yourself →
          </button>
        </div>
      )}

      {/* Mobile cinematic strip: between the CTA and the InputBar wrapper.
         Self-gates via VITE_ONBOARDING_COACH_ENABLED + localStorage flag.

         2026-06-04 (Matt, mobile prod): the blue "Try for yourself" button and
         this coach sticky note were stacking directly on top of each other and
         reading as TWO competing calls-to-action for the same thing. The blue
         `empty-state-cta` button is the canonical CTA (analytics:
         trackTryForYourself); the coach's job is the pre-result 1-of-5 → 5-of-5
         walkthrough that teaches the input + step flow. Once the watch-gate
         opens (demoCompleted=true → the blue CTA appears) the walkthrough has
         done its job, so on mobile we unmount the coach and let the single blue
         CTA stand alone — mirroring desktop, where the coach lives in a far-
         right gutter and never stacks under the CTA. The 1-5 step progression
         still plays for first-time visitors while demoCompleted is false.
         (Desktop dismiss-controls specs are unaffected: they run at 1480px,
         i.e. the non-mobile path below.) */}
      {isMobile && !demoCompleted && (
        <OnboardingCoach
          phase={coachPhase}
          isMobile
          showDismissControls={isSignedIn && demoCompleted}
          onSessionDismiss={sessionDismiss}
          onPersistentDismiss={persistentDismiss}
          streamingPastSubmitHighlight={streamingPastSubmitHighlight}
        />
      )}

      {/* InputBar height changes as the textarea fills + the CompiledQueryCard
         mounts during the parsing/lanes phases. Reserve the max height so the
         CTA above doesn't drift (measured 2026-05-25: 234px desktop /
         ~270px mobile worst case, slightly padded for safety). */}
      <div
        data-testid="empty-state-input-wrapper"
        // Reserve enough vertical space for the WORST-CASE InputBar height
        // across all demo phases. Measured 2026-05-26 on the new (slower)
        // timing: during parsing the CompiledQueryCard mounts and the bar
        // grows to ~247px on mobile / ~269px on desktop.
        // - Desktop: 270 (keeps CTA stable; button-stability spec enforces
        //   ≤4px drift).
        // - Mobile: 210 (was 250; trimmed again 2026-06-04 as part of the
        //   input-bar-off-screen fix — see the 3-zone reserve budget math
        //   above). The peak compiled-card growth is transient; an extra
        //   inline layout shift is acceptable on mobile in exchange for the
        //   input bar staying visible above the fold on iPhone 14 + S22.
        style={{ minHeight: isMobile ? 210 : 270 }}
      >
        <InputBar
          videoSource={fakeVideoSource}
          onClearVideo={dismiss}
          prompt={displayedPrompt}
          onPromptChange={(next) => {
            // Any keystroke that diverges from the autoplayed text = user takeover.
            // The autoplay code calls setDisplayedPrompt directly (not through this
            // handler), so any inbound onPromptChange is by definition the user.
            if (next !== displayedPrompt) dismiss()
          }}
          onSubmit={dismiss}
          onOpenVideoMenu={() => handleTryForYourself()}
          onRequestFile={() => handleTryForYourself()}
          processOffline={false}
          onProcessOfflineChange={() => dismiss()}
          disabled={false}
          isStreaming={false}
          promptSent={false}
          hasResultPills={false}
          isMobile={isMobile}
          compiledQuery={fixture.compiledQuery}
          parseState={parseState}
          parseError={null}
          /* No real cameras — fixture is the source of truth. */
          cameraDevices={[]}
          selectedCameraId={null}
          hideVideoChip
          sourceModeOverride="webcam"
          planTier="free"
        />
      </div>

      {showCompareButton && compareFixture && (
        <CompareToGeminiModal
          open={compareOpen}
          fixture={compareFixture}
          onClose={() => setCompareOpen(false)}
          isMobile={isMobile}
        />
      )}
    </div>
  )

  // OnboardingHighlights — the yellow scribble / cyan glow that draws the
  // eye to whichever surface area the current step is happening on. It's
  // position:fixed so adding it doesn't perturb the surrounding layout
  // (the button-stability spec measures CTA y-position at 4px tolerance).
  const highlights = <OnboardingHighlights phase={coachPhase} />

  if (isMobile) {
    return (
      <>
        {demoColumn}
        {highlights}
      </>
    )
  }

  // Desktop: when the viewport is too narrow for the sticky-note coach, we
  // return the demoColumn DIRECTLY (no wrapper) so the layout is byte-for-byte
  // identical to pre-2026-05-26. This is critical because
  // onboarding-button-stability.spec.ts samples CTA y-position across phases
  // at 4px tolerance — even introducing a wrapper div around demoColumn shifts
  // measurements at parsing-phase transitions when the InputBar's compiled
  // query card mounts and the parent's flex centering recomputes.
  if (!coachVisible) {
    return (
      <>
        {demoColumn}
        {highlights}
      </>
    )
  }

  // Wide-viewport desktop: the demo column keeps its natural centered
  // max-width (so the input/result pill render at their normal size). The
  // OnboardingCoach is positioned absolutely to the right of the demo column
  // with a 24px gutter. Matt 2026-05-26: "on the web it takes up the
  // horizontal space of the input and results pill. They should remain their
  // normal width, and the sidebar should be on the far right of the screen."
  //
  // Geometry: the parent (DesktopLayout) is maxWidth 900 centered. We anchor
  // the coach at `left: calc(50% + 450px + 24px)` so it sits 24px right of
  // the demo's right edge. coachVisible gate (viewport >= 1480px) prevents
  // the coach from extending past the viewport.
  return (
    <div
      style={{
        width: '100%',
        position: 'relative',
      }}
    >
      {demoColumn}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 'calc(50% + 450px + 24px)',
          zIndex: 4,
          pointerEvents: 'auto',
        }}
      >
        <OnboardingCoach
          phase={coachPhase}
          isMobile={false}
          showDismissControls={isSignedIn && demoCompleted}
          onSessionDismiss={sessionDismiss}
          onPersistentDismiss={persistentDismiss}
          streamingPastSubmitHighlight={streamingPastSubmitHighlight}
        />
      </div>
      {highlights}
    </div>
  )
}

export default EmptyStateDemo

// ───────────────────────────────────────────────────────────────────────────
// DemoMobilePinnedMock — 2026-05-28 (Matt #engineering-jeff)
//
// Mobile streaming-phase mock of the actual product's 3-zone pinned layout.
// The real product (MobileLayout.tsx) renders:
//   Zone 1 (top, position:fixed)   — live camera preview
//   Zone 2 (below, position:fixed) — ResultPillComp with pinnedMode (active pill)
//   Zone 3 (scrollable)            — thread history of prior result pills
//
// In the demo we can't acquire a real camera (no permission, no WebRTC). So we
// statically mock ALL THREE zones using IN-FLOW boxes (NOT position:fixed) so
// the demo viewport keeps reserving a fixed vertical space. Matt 2026-06-04:
// "shouldn't the mobile coaching walkthrough replicate the pinned 3 section
// layout of the actual mobile UX? It seems to be using the older mobile
// layout" — the prior mock only rendered zones 1 + 2. Now all three render.
// The narrative log inside zone 2 is the focal point of step 5 — it gets the
// cyan glow via `data-testid="narrative-log"` from <NarrativeLog>.
//
// Why this exists: prior to 2026-05-28 the demo used the generic full-pill
// rendering with a "Video coming soon" placeholder. Matt: "the camera view
// etc is not showing the recent 'pinned' camera view and narrative text
// block. This part of the coaching video walkthrough needs redoing to
// reflect the current product UX."
// ───────────────────────────────────────────────────────────────────────────
function DemoMobilePinnedMock({ pill }: { pill: ResultPill }): ReactElement {
  return (
    <div
      data-testid="demo-mobile-pinned-mock"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 12,
        overflow: 'hidden',
        border: `1px solid ${L.border}`,
        background: L.bgPrimary,
      }}
    >
      {/* Zone 1 — "Your camera" view. Shows the REAL forklift demo clip
         (autoplay + loop + muted, no controls) behind the product's only real
         camera affordance (the "Your camera" live pill), so the mobile
         coaching walkthrough matches desktop and the prod product. There is
         NO detection / bounding-box overlay: the product does not draw one,
         and the fabricated "FORKLIFT · 91%" dashed box was removed 2026-06-04
         (Matt flagged it as a hallucinated affordance that doesn't exist in
         the real product).
         Before 2026-06-04 this was a hardcoded SVG silhouette — Matt flagged
         "I don't see the video on prod on mobile" because the real clip never
         rendered on this path. Fixed 110px height (instead of true 16:9 ≈
         191px on a 340px-wide demo) so the input bar stays inside iPhone 14 →
         Galaxy S22 viewport budgets. Tag testid `demo-camera-mock`. */}
      <div
        data-testid="demo-camera-mock"
        aria-hidden="true"
        style={{
          position: 'relative',
          flexShrink: 0,
          // True 16:9 box so the 854×480 forklift clip shows at its real
          // proportions (Matt 2026-06-04: "the camera/video is cropped and not
          // at regular proportions/height"). The prior fixed 110px height +
          // objectFit:cover cropped the top/bottom of the frame. aspectRatio
          // keeps it correct across viewport widths; objectFit:contain (below)
          // letterboxes rather than crops. Still well within the iPhone 14 →
          // Galaxy S22 input-bar budget (~191px tall at a 340px-wide demo).
          aspectRatio: '16 / 9',
          width: '100%',
          background:
            // Subtle radial sheen so it doesn't look like a flat dead box.
            'radial-gradient(120% 80% at 30% 30%, rgba(27,79,224,0.18), rgba(13,15,22,0.92) 70%), #0B0E15',
          overflow: 'hidden',
        }}
      >
        {/* Real forklift clip — fills the zone at correct proportions, plays
           unattended. objectFit:contain so the full frame is visible (no crop).
           Sits at z-index 0 under the "Your camera" label pill. */}
        {pill.videoUrl && (
          <video
            data-testid="demo-mobile-camera-video"
            ref={(el) => { if (el) { el.muted = true; el.play().catch(() => {}); } }}
            src={pill.videoUrl}
            poster={pill.videoThumb || undefined}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'contain', zIndex: 0, background: '#0B0E15',
            }}
            {...({ 'webkit-playsinline': 'true' } as React.HTMLAttributes<HTMLVideoElement>)}
          />
        )}
        {/* Label — mimic the product's camera "live" affordance. */}
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 8px',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.45)',
            color: '#fff',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 9,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#16A34A',
              boxShadow: '0 0 6px rgba(22,163,74,0.9)',
              animation: 'pv2-blink 1.4s ease-in-out infinite',
            }}
          />
          Your camera
        </div>
        {/* No detection / bounding-box overlay here on purpose. The real
           product shows only the live camera feed — it does NOT draw a
           dashed bounding box or a "FORKLIFT · 91%" confidence label over the
           frame. A fabricated overlay was removed 2026-06-04 (Matt: it read as
           a hallucinated detection that the product never produces). */}
      </div>

      {/* Zone 2 — Pinned active-pill block using the real ResultPillComp in
         pinnedMode. This is the exact rendering path the production mobile
         streaming layout uses (MobileLayout Zone 2). Real layout pins this at
         height clamp(180px,35vh,280px) directly below the camera; we mirror
         that with flexShrink:0 + the same clamp so the proportions match.
         Carries `data-testid="narrative-log"` through to the inner
         NarrativeLog, so OnboardingHighlights' step-5 cyan glow lands here. */}
      <div
        data-testid="demo-pinned-pill-mock"
        style={{
          flexShrink: 0,
          // Trimmed 2026-06-04 (clamp 120-165 → 110-150) so the 3-zone reserve
          // fits inside the 360-400 mobile budget without pushing the input
          // bar off-screen. Still mirrors the real product's Zone-2 proportions
          // (MobileLayout uses clamp(180,35vh,280); the demo is a compact mock).
          height: 'clamp(110px, 20vh, 150px)',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          borderTop: `1px solid ${L.border}`,
          background: L.bgPrimary,
          overflow: 'hidden',
        }}
      >
        <ResultPillComp
          pill={pill}
          isMobile
          pinnedMode
          remoteStream={null}
          localStream={null}
          slotRevealed
          noFadeIn
          isStreamActive
          isSaving={false}
          canDownload={false}
          planTier="free"
        />
      </div>

      {/* Zone 3 — scrollable thread history. In the real product this is the
         `thread-area` zone below the two fixed zones, holding prior result
         pills. The demo mocks it with a compact, scrollable stack of
         previous-result chips so the 3-zone structure reads correctly
         (Matt 2026-06-04). It fills the remaining vertical space and scrolls
         independently, exactly like the production thread area. */}
      <div
        data-testid="demo-thread-area-mock"
        aria-hidden="true"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          borderTop: `1px solid ${L.border}`,
          background: L.bgSecondary,
          padding: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {[
          { q: 'Any people in frame?', a: 'No people detected.' },
          { q: 'Is the loading dock clear?', a: 'Yes — dock area is clear.' },
        ].map((item, i) => (
          <div
            key={i}
            style={{
              flexShrink: 0,
              borderRadius: 10,
              border: `1px solid ${L.border}`,
              background: L.bgPrimary,
              padding: '8px 10px',
              opacity: 0.62,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, color: L.textPrimary, marginBottom: 2 }}>{item.q}</div>
            <div style={{ fontSize: 11, color: L.textMuted }}>{item.a}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
